import { FakePageData, SelectorFakeDataValuePair } from 'internalLibrary';
import {
  generateBankAccountNumber,
  generateEmail,
  generateFirstName,
  generateLastName,
  generateNumber,
  generatePassword,
  generatePersonalID,
  generatePhoneNumber,
  PersonalIdOptions,
} from './fakeDataGenerators';
import { QuestionnaireFormQuestion } from 'types/ui';

export type WidnowWithFakeData = Window &
  typeof globalThis & {
    fakeData?: { [key: string]: any };
  };

type ResultType = 'success' | 'fail';

const getIncomeFakeFields = (type: ResultType): SelectorFakeDataValuePair[] => {
  let monthlyOtherLoans = generateNumber(0, 8000);
  let monthlyHousingCosts = generateNumber(8000, 20000);
  let monthlyOtherLivingCosts = generateNumber(8000, 18000);

  let numberOfChildrenInHouse = generateNumber(0, 10);
  let numberOfAdultsInHouse = generateNumber(1, 10);

  const ageOptions: PersonalIdOptions = {};

  const getCosts = () =>
    Number(monthlyOtherLoans) +
    Number(monthlyHousingCosts) +
    Number(monthlyOtherLivingCosts);
  let monthlyIncome = String(getCosts());

  switch (type) {
    case 'success':
      monthlyOtherLoans = '0';
      monthlyHousingCosts = '10000';
      monthlyOtherLivingCosts = '4000';
      monthlyIncome = String(
        Number(getCosts()) + Number(generateNumber(5000, 25000)),
      );
      numberOfChildrenInHouse = '0';
      numberOfAdultsInHouse = '2';

      ageOptions.maxAge = 45;
      ageOptions.minAge = 26;
      break;

    case 'fail':
      monthlyIncome = String(
        Number(getCosts()) + Number(generateNumber(500, 2500)),
      );
      numberOfChildrenInHouse = generateNumber(3, 10);
      numberOfAdultsInHouse = '1';
      break;
  }

  const personalIdParts = generatePersonalID(ageOptions).split('-');

  return [
    ['[name="prePersonalId"]', personalIdParts[0]],
    ['[name="postPersonalId"]', personalIdParts[1]],
    ['[name="monthlyIncome"]', monthlyIncome], // must be highest from the monthly amounts
    ['[name="monthlyOtherLoans"]', monthlyOtherLoans],
    ['[name="monthlyHousingCosts"]', monthlyHousingCosts],
    ['[name="monthlyOtherLivingCosts"]', monthlyOtherLivingCosts],
    ['[name="numberOfChildrenInHouse"]', numberOfChildrenInHouse],
    ['[name="numberOfAdultsInHouse"]', numberOfAdultsInHouse],
  ];
};

const registerGeneralFakeFields = (
  type: ResultType,
): SelectorFakeDataValuePair[] => {
  const personalIdParts = generatePersonalID().split('-');

  return [
    ['[name="email"]', generateEmail],
    ['[name="phone"]', generatePhoneNumber],
    ['[name="firstName"]', generateFirstName],
    ['[name="lastName"]', generateLastName],
    ['[name="prePersonalId"]', personalIdParts[0]],
    ['[name="postPersonalId"]', personalIdParts[1]],
    ['[name="bankAccountClearingNumber"]', '9500'],
    ['[name="bankAccountNumber"]', generateBankAccountNumber],
    ['[name="password"]', generatePassword],
    ['[name="agreement1"]', 'checkbox'],
    ['[name="agreement2"]', 'checkbox'],
    ...getIncomeFakeFields(type),
    ['[data-test="button_submit"]', 'button'],
  ];
};
const registerSuccessFakeFields = registerGeneralFakeFields('success');
const registerFailFakeFields = registerGeneralFakeFields('fail');

const getRandomAnswer = (
  question: QuestionnaireFormQuestion,
  skip?: string[],
): string => {
  const randomAnswer =
    question.answers[Math.floor(Math.random() * question.answers.length)];
  const randomAnswerId = question.id + '_' + randomAnswer.id;

  if (skip !== undefined && skip.includes(randomAnswerId)) {
    return getRandomAnswer(question, skip);
  }

  return randomAnswerId;
};

const getFakeAnswerFromWindow = (name: string, skip?: string[]) => {
  const fakeData = (window as WidnowWithFakeData).fakeData;
  if (fakeData !== undefined) {
    const fakeDataValue = fakeData[name];
    if (fakeDataValue !== undefined) {
      return getRandomAnswer(fakeDataValue, skip);
    }
  }
  return '';
};

const questionnaireFakeFields: SelectorFakeDataValuePair[] = [
  ['[name="PEP"]', () => getFakeAnswerFromWindow('PEP')],
  ['[name="BEHALF"]', () => getFakeAnswerFromWindow('BEHALF')],
  ['[name="JOB"]', () => getFakeAnswerFromWindow('JOB')],
  ['[name="PROF_FIELD"]', () => getFakeAnswerFromWindow('PROF_FIELD')],
  ['[name="LOANS_PLANNED"]', () => getFakeAnswerFromWindow('LOANS_PLANNED')],
  ['[name="MONEY_PURPOSE"]', () => getFakeAnswerFromWindow('MONEY_PURPOSE')],
  ['[data-test="button_submit"]', 'button'],
];

const questionnaireSuccessFakeFields: SelectorFakeDataValuePair[] = [
  ['[name="PEP"]', () => '2_4'], // false
  ['[name="BEHALF"]', '100_101'], // true
  [
    '[name="JOB"]',
    () =>
      getFakeAnswerFromWindow('JOB', [
        '5_7',
        '5_9',
        '5_11',
        '5_13',
        '5_10',
        '5_9',
      ]),
  ],
  [
    '[name="PROF_FIELD"]',
    () => getFakeAnswerFromWindow('PROF_FIELD', ['14_15']),
  ],
  ['[name="LOANS_PLANNED"]', '36_37'],
  ['[name="MONEY_PURPOSE"]', () => getFakeAnswerFromWindow('MONEY_PURPOSE')],
  ['[data-test="button_submit"]', 'button'],
];

export const fakeDataConfig: FakePageData[] = [
  {
    url: '/first-loan/register',
    items: {
      fail: registerFailFakeFields,
      success: registerSuccessFakeFields,
    },
  },
  {
    url: '/first-loan/questionnaire',
    items: {
      random: questionnaireFakeFields,
      success: questionnaireSuccessFakeFields,
    },
  },
];
